import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * NewVersionHint — tells a long-lived tab that it's running stale code.
 *
 * WHY THIS EXISTS (Walter 2026-08-09). He hiked with no coverage, typed on
 * his phone, came home and typed on his PC, and got a genuine two-way
 * divergence on prod: each device showing its own lines, no conflict band,
 * both claiming "synced". Reloading the phone changed nothing; reloading the
 * PC fixed it instantly. The PC had been sitting on a bundle from before the
 * merge-on-receive deploy (#164), so its half of the exchange still used the
 * old adopt-by-overwrite path. We lost an evening to a stale tab.
 *
 * The app ALREADY had auto-update wiring in index.js — but it hangs off the
 * service worker's `updatefound`, and `/reminder-sw.js` is a static file in
 * public/. A normal deploy ships a new `main.<hash>.js` and a new index.html
 * while the service worker stays BYTE-IDENTICAL, so `reg.update()` finds
 * nothing to update, `updatefound` never fires, and the reload never happens.
 * That mechanism can only ever notice deploys where we happened to edit the
 * service worker itself. This is not a tuning problem; it watches the wrong
 * file.
 *
 * So watch something that changes on EVERY deploy: `asset-manifest.json`,
 * which CRA emits with the current hashed bundle name. Compare its `main.js`
 * against the bundle this tab actually booted from. Different ⇒ a newer
 * version is live and this tab isn't running it.
 *
 * Deliberately a HINT, not an auto-reload: index.js already auto-reloads on a
 * real service-worker update, and yanking the page out from under someone
 * mid-sentence is its own bug (see the isEditing guard there). This asks.
 */

// The banner is suppressed on marketing routes — same rule as
// ExtensionBlockHint. Nothing there depends on app code being current.
const MARKETING_ROUTE = /^\/(landing|about|help|privacy|terms|contact)\b/;

// Dismissal is a SNOOZE, never permanent. A permanently-dismissed update
// notice would recreate the exact failure this exists to prevent.
const SNOOZE_KEY = 'lists_new_version_snoozed_until';
const SNOOZE_MS = 60 * 60 * 1000;

const POLL_MS = 15 * 60 * 1000;        // background re-check while the tab lives
const FOREGROUND_THROTTLE_MS = 5 * 60 * 1000; // on tab refocus, at most this often

/**
 * Extract the bundle filename from a script src / manifest path.
 * "/static/js/main.51c2d482.js" → "main.51c2d482.js"
 */
export function bundleName(src) {
  if (!src || typeof src !== 'string') return null;
  const clean = src.split('?')[0].split('#')[0];
  const last = clean.slice(clean.lastIndexOf('/') + 1);
  return last || null;
}

/**
 * isStaleBuild(running, deployed) → boolean
 *
 * True only when we can positively identify BOTH sides as production-hashed
 * bundles AND they differ. Anything unknown, unhashed (the CRA dev server
 * serves an unhashed bundle.js) or unparseable is NOT stale — a false
 * "reload" prompt trains people to ignore the real one.
 */
export function isStaleBuild(running, deployed) {
  const HASHED = /^main\.[a-z0-9]+\.js$/i;
  const a = bundleName(running);
  const b = bundleName(deployed);
  if (!a || !b) return false;
  if (!HASHED.test(a) || !HASHED.test(b)) return false;
  return a !== b;
}

// The bundle THIS tab booted from, read once at module load: index.html
// carries <script defer src="/static/js/main.<hash>.js">.
function runningBundle() {
  try {
    const el = document.querySelector('script[src*="/static/js/main."]');
    return el ? bundleName(el.getAttribute('src')) : null;
  } catch {
    return null;
  }
}

// What's deployed right now. cache:'no-store' so neither the HTTP cache nor
// the service worker can answer with the copy that shipped alongside our own
// stale bundle — that would make this check permanently self-confirming.
async function deployedBundle() {
  const res = await fetch('/asset-manifest.json', {
    cache: 'no-store',
    signal: AbortSignal.timeout ? AbortSignal.timeout(8000) : undefined,
  });
  if (!res.ok) return null;
  // A missing file gets SPA-rewritten to index.html, so this can be HTML.
  // res.json() throws on that, which the caller treats as "don't know".
  const manifest = await res.json();
  return bundleName(manifest?.files?.['main.js']);
}

function snoozedUntil() {
  try { return parseInt(localStorage.getItem(SNOOZE_KEY) || '0', 10) || 0; }
  catch { return 0; }
}

export default function NewVersionHint() {
  const [stale, setStale] = useState(false);
  const [snoozed, setSnoozed] = useState(() => Date.now() < snoozedUntil());
  const lastCheckRef = useRef(0);
  const location = useLocation();

  const check = useCallback(async () => {
    if (stale) return;                    // already known; nothing to learn
    if (!navigator.onLine) return;        // can't ask, and reloading offline is worse
    const running = runningBundle();
    if (!running) return;
    lastCheckRef.current = Date.now();
    try {
      const deployed = await deployedBundle();
      if (isStaleBuild(running, deployed)) {
        setStale(true);
        // Surface it in the console too: when Walter reports something odd,
        // "which build is this tab on?" is the first question, and this
        // answers it without a screenshot.
        try {
          console.info(
            `[listicity] this tab is running ${running}; ${deployed} is deployed — reload to update.`,
          );
        } catch { /* ignore */ }
      }
    } catch {
      // Offline, blocked, SPA-rewritten HTML, malformed JSON — all mean
      // "don't know", and don't-know must never render a prompt.
    }
  }, [stale]);

  useEffect(() => {
    // Let the app finish booting before spending a request on this.
    const first = setTimeout(check, 8000);
    const poll = setInterval(check, POLL_MS);
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastCheckRef.current < FOREGROUND_THROTTLE_MS) return;
      check();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', check);
    // Debug hook: forces a check without waiting out the poll. Used to
    // verify this end-to-end against a real deploy.
    window.__listicityCheckUpdate = check;
    return () => {
      clearTimeout(first);
      clearInterval(poll);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', check);
      if (window.__listicityCheckUpdate === check) delete window.__listicityCheckUpdate;
    };
  }, [check]);

  const reload = async () => {
    // The service worker serves navigations network-first with a 1.2s timeout
    // and falls back to cache, so on a slow link a plain reload can be handed
    // the very index.html we're trying to escape. Drop cached navigation
    // entries first — best-effort, time-boxed, and never allowed to block the
    // reload itself.
    try {
      await Promise.race([
        (async () => {
          const keys = await caches.keys();
          await Promise.all(keys.map(async (k) => {
            const cache = await caches.open(k);
            await Promise.all([
              cache.delete('/'),
              cache.delete('/index.html'),
              cache.delete(window.location.pathname),
            ].map(p => p.catch(() => {})));
          }));
        })(),
        new Promise(r => setTimeout(r, 1500)),
      ]);
    } catch { /* non-fatal */ }
    window.location.reload();
  };

  const snooze = () => {
    try { localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS)); } catch {}
    setSnoozed(true);
  };

  if (!stale || snoozed || MARKETING_ROUTE.test(location.pathname) || location.pathname === '/') {
    return null;
  }

  return (
    <div
      role="status"
      style={{
        position: 'fixed',
        bottom: 0, left: 0, right: 0,
        zIndex: 1750, // below the extension banner (1800): that one is broken-sync, this is housekeeping
        display: 'flex',
        justifyContent: 'center',
        pointerEvents: 'none',
        padding: '10px',
      }}
    >
      <div
        style={{
          maxWidth: 560,
          width: '100%',
          background: 'var(--surface2)',
          color: 'var(--text)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          padding: '10px 14px',
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 10,
          boxShadow: '0 6px 16px rgba(0,0,0,0.18)',
          fontFamily: 'var(--mono)',
          fontSize: 'calc(11px * var(--ui-scale, 1))',
          lineHeight: 1.45,
          pointerEvents: 'auto',
        }}
      >
        <span style={{ flex: '1 1 260px', minWidth: 0 }}>
          a newer version of listicity is available — this tab is still running
          the older one.
        </span>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <button
            type="button"
            onClick={reload}
            title="reload this tab to run the current version"
            style={{
              background: 'var(--accent)',
              color: 'var(--bg)',
              border: '1px solid var(--accent)',
              borderRadius: 'var(--radius)',
              padding: '4px 12px',
              cursor: 'pointer',
              fontFamily: 'var(--mono)',
              fontSize: 'calc(10px * var(--ui-scale, 1))',
              fontWeight: 600,
            }}
          >
            reload
          </button>
          <button
            type="button"
            onClick={snooze}
            title="hide for an hour"
            style={{
              background: 'transparent',
              color: 'var(--text-dim)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius)',
              padding: '4px 10px',
              cursor: 'pointer',
              fontFamily: 'var(--mono)',
              fontSize: 'calc(10px * var(--ui-scale, 1))',
            }}
          >
            later
          </button>
        </div>
      </div>
    </div>
  );
}
